import { Link } from 'react-router-dom';
import { usePageMeta, useJsonLd } from '@/hooks/usePageMeta';

const MILESTONES = [
  { icon: 'ri-eye-line', title: '看见真实', desc: '做求职者与员工的信息之眼，把散落各处的真实职场反馈聚拢成可参考的公共信息。' },
  { icon: 'ri-voiceprint-line', title: '尊重每一个声音', desc: '让前员工、知情人与企业都能在规则内说话，避免单方面「带节奏」与人身攻击。' },
  { icon: 'ri-vip-crown-line', title: '守住可信底线', desc: '宁可慢一点、少一点，也要让每一条升级为「已核实」的内容经得起多方对照。' },
];

const PRINCIPLE_LIST = [
  { t: '事实优先', d: '缺证不妄断，重要结论需多方印证。' },
  { t: '分级标注', d: '已核实 / 待核实 / 持续更新，状态透明。' },
  { t: '匿名保护', d: '投稿来源与个人身份严格打码。' },
  { t: '允许发声', d: '重大内容先给相关方回应机会。' },
  { t: '及时勘误', d: '有错必纠，公开更正。' },
  { t: '不取标题党', d: '拒绝用情绪与惊悚标题收割流量。' },
];

const CONTACT_CHANNELS = [
  { icon: 'ri-mail-send-fill', name: '投稿 / 线索邮箱', value: 'tougao@51baoliao.example', desc: '匿名提交你的爆料线索，24 小时内响应' },
  { icon: 'ri-error-warning-fill', name: '勘误 / 投诉', value: 'fankui@51baoliao.example', desc: '内容失实、隐私或名誉相关问题请走此通道' },
  { icon: 'ri-team-fill', name: '合作 / 转载', value: 'hezuo@51baoliao.example', desc: '媒体合作、内容转载授权与商务沟通' },
];

export default function About() {
  usePageMeta(
    {
      title: '关于我们 - 51爆料的定位、理念与内容原则 | 51爆料',
      desc: '了解51爆料：一个聚焦内部消息与员工爆料的职场信息社区。认识我们的品牌定位、发展理念、内容原则、用户口碑与联系方式。',
    },
    '/about'
  );
  useJsonLd('ld-about', {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51爆料',
    url: 'https://00279.top/about',
    description: '51爆料是聚焦内部消息与员工爆料的职场信息社区，坚持匿名投稿、人工核实与理性表达。',
    mainEntity: {
      '@type': 'Organization',
      name: '51爆料',
      slogan: '看清公司真实情况，掌握别人不知道的内部动态',
      email: 'tougao@51baoliao.example',
    },
  });

  return (
    <div className="bg-background-50">
      {/* 页首 */}
      <section className="border-b border-background-200 bg-background-100/60 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="flex items-center gap-1 hover:text-primary-600">
              <i className="ri-home-4-line" aria-hidden="true"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-400">关于我们</span>
          </nav>
          <div className="mt-5 flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary-600">
                <i className="ri-information-line" aria-hidden="true"></i>
                About 51爆料
              </p>
              <h1 className="mt-2 font-display text-3xl font-black text-foreground-950 md:text-4xl">
                我们想让每一份「内部消息」，都更有价值
              </h1>
              <p className="mt-3 max-w-2xl text-[15px] leading-8 text-foreground-600">
                51爆料是一个聚焦内部消息与员工爆料的职场信息社区。我们相信：求职者在做决定前，有权看到一家公司的真实口碑；在岗员工也有权在不泄露机密的前提下，说出真实的一线处境。
              </p>
            </div>
            <span className="flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2.5 text-xs font-semibold whitespace-nowrap text-background-50">
              <i className="ri-heart-3-fill" aria-hidden="true"></i>
              始于 2024 · 愿与 12w+ 职场人同行
            </span>
          </div>
        </div>
      </section>

      {/* 品牌故事 */}
      <section className="py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <img
              src="https://readdy.ai/api/search-image?query=Editorial%20illustration%20of%20diverse%20professionals%20gathered%20around%20a%20large%20shared%20document%20with%20megaphone%20and%20lightbulb%20motif%20in%20warm%20ink%20silhouette%2C%20retro%20newsprint%20cream%20texture%2C%20vermilion%20red%20and%20amber%20accents%2C%20no%20text&width=1000&height=760&seq=about-brand-story&orientation=portrait"
              alt="51爆料编辑部汇集多方声音的品牌插画"
              className="h-auto w-full rounded-lg object-cover"
            />
          </div>
          <div className="lg:col-span-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary-600">
              <i className="ri-quill-pen-line" aria-hidden="true"></i>
              品牌故事
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">从一次「差点入坑」开始的社区</h2>
            <p className="mt-4 text-[15px] leading-8 text-foreground-700">
              51爆料的起点，是一群朋友聚会时的吐槽：有人差点接了某家正在收缩部门的 offer，有人直到离职清算才发现公积金口径和当初谈的不一样。信息的不对称，让每一个普通打工人都在无形中付出代价。
            </p>
            <p className="mt-3 text-[15px] leading-8 text-foreground-700">
              于是我们决定，把那些散落在酒桌、微信群和匿名帖里的真实职场反馈，做成一个透明、可分级核实的信息池。不做夸大，不博眼球，只求让读者在跳槽、谈判与选择前，多掌握一点真实情况。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {[
                { n: '12w+', l: '月活读者' },
                { n: '6', l: '行业栏目' },
                { n: '8700+', l: '累计核实投稿' },
              ].map((s) => (
                <div key={s.l} className="rounded-lg border border-background-200 bg-background-100/60 p-4 text-center">
                  <p className="font-display text-xl font-black text-primary-700 md:text-2xl">{s.n}</p>
                  <p className="mt-1 text-xs text-foreground-600">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="border-y border-background-200 bg-background-100/60 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-accent-700">
              <i className="ri-compass-3-line" aria-hidden="true"></i>
              定位与理念
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">
              我们不制造内幕，只是让真实的信息流动
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              51爆料定位于「职场透明化推动者」，既不站队资本、也不煽动对立。我们搭建的是一座连接前员工、知情人、企业与求职者的信息桥梁。
            </p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {MILESTONES.map((m, i) => (
              <div key={m.title} className="relative rounded-lg border border-background-200 bg-background-50 p-7">
                <span className="absolute right-5 top-4 font-display text-4xl font-black text-background-300">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500 text-xl text-background-50">
                  <i className={m.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-display text-lg font-bold text-foreground-950">{m.title}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-700">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section id="principles" className="scroll-mt-24 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary-600">
                <i className="ri-secure-payment-line" aria-hidden="true"></i>
                内容原则
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">我们坚持的六条底线</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                平台能走多远，取决于读者信不信。所以从第一篇内容起，我们就用机制而不是口号来约束自己。
              </p>
              <Link
                to="/faq"
                className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs font-semibold whitespace-nowrap text-foreground-800 hover:border-primary-400 hover:text-primary-700"
              >
                了解常见问题
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:col-span-8">
              {PRINCIPLE_LIST.map((p) => (
                <div key={p.t} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100/50 p-5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary-600 text-background-50">
                    <i className="ri-check-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-950">{p.t}</h3>
                    <p className="mt-1 text-xs leading-6 text-foreground-600">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 口碑 */}
      <section className="relative overflow-hidden bg-foreground-950 py-12 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-accent-400">
            <i className="ri-voiceprint-line" aria-hidden="true"></i>
            用户口碑
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-background-50 md:text-3xl">来自社区的真实声音</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              { q: '它让我在面试前，提前判断出了一家正在收缩的业务线。', w: '求职中的产品经理' },
              { q: '「待核实」三个字很诚实，说明真有人在认真做事。', w: '关注职场信息多年' },
              { q: '匿名投稿没被追问身份，编辑只核对事实，这点让我安心。', w: '一位前大厂员工' },
            ].map((x) => (
              <figure key={x.w} className="rounded-lg border border-background-200/20 bg-background-50/5 p-6">
                <span className="flex gap-0.5 text-accent-400" aria-hidden="true">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </span>
                <blockquote className="mt-3 text-sm leading-7 text-background-200">「{x.q}」</blockquote>
                <figcaption className="mt-4 text-xs text-background-400">{x.w}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 联系 */}
      <section id="contact" className="scroll-mt-24 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-primary-600">
              <i className="ri-mail-line" aria-hidden="true"></i>
              联系我们
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">让消息找到我们，让答案找到你</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-foreground-600">
              无论是匿名投稿、勘误投诉还是商务合作，都欢迎通过以下渠道与我们取得联系。
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {CONTACT_CHANNELS.map((c) => (
              <a
                key={c.name}
                href={`mailto:${c.value}`}
                className="group rounded-lg border border-background-200 bg-background-100/50 p-6 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500 text-xl text-background-50">
                  <i className={c.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-display text-base font-bold text-foreground-950">{c.name}</h3>
                <p className="mt-1 break-all text-sm font-medium text-primary-700">{c.value}</p>
                <p className="mt-2 text-xs leading-6 text-foreground-600">{c.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-foreground-800 transition-colors group-hover:text-primary-700">
                  立即联系 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </a>
            ))}
          </div>
          <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-lg border border-dashed border-background-300 bg-background-100/40 p-6 sm:flex-row">
            <p className="flex items-center gap-2 text-sm text-foreground-700">
              <i className="ri-time-line text-primary-600" aria-hidden="true"></i>
              工作日 10:00 - 19:00 在线，紧急纠错请邮件说明「加急」
            </p>
            <span className="flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-2 text-xs text-secondary-900">
              <i className="ri-shield-check-line" aria-hidden="true"></i>
              我们承诺在 24h 内响应每封来信
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}